/**
 * Browser storage keys are scoped by {@link SystemConfig.basePath} so multiple app instances
 * on the same origin (e.g. /list-dev vs /list2) do not clobber each other.
 *
 * Format: `{basePath}/{logicalKey}` when basePath is non-empty after normalization; otherwise `logicalKey`.
 *
 * `window.__LIST_STORAGE_BASE_PATH__` is set from the root layout before client scripts run so
 * Zustand persist can rehydrate with the correct prefix even when {@link useConfigStore} is not
 * initialized yet.
 */

import { createJSONStorage, StateStorage } from 'zustand/middleware';
import { useConfigStore } from '@/stores/configStore';

declare global {
  interface Window {
    __LIST_STORAGE_BASE_PATH__?: string;
  }
}

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

/**
 * Normalizes base path: trim, strip trailing slashes. Returns empty string when unset or root-only.
 */
export function normalizeScopedBasePath(basePath: string | undefined | null): string {
  if (basePath == null || basePath === '/') return '';
  const t = basePath.trim();
  if (!t || t === '/') return '';
  return t.replace(/\/+$/, '');
}

/**
 * Resolved storage prefix (`basePath` without trailing slash), or empty string for the root app instance.
 */
export function getScopedStorageBasePathSync(): string {
  if (typeof window !== 'undefined') {
    const w = normalizeScopedBasePath(window.__LIST_STORAGE_BASE_PATH__);
    if (w) return w;
  }
  return normalizeScopedBasePath(useConfigStore.getState().systemConfig?.basePath ?? '');
}

/**
 * Full key used with `localStorage` / `sessionStorage`: `{prefix}/{logical}` or `logical` when prefix is empty.
 *
 * @param logicalKey Stable app-owned suffix (must not include another storage root).
 */
export function scopedStorageKey(logicalKey: string): string {
  const prefix = getScopedStorageBasePathSync();
  const key = logicalKey.startsWith('/') ? logicalKey.replace(/^\/+/, '') : logicalKey;
  if (!key) throw new Error('scopedStorageKey: logicalKey must not be empty');
  if (!prefix) return key;
  return `${prefix}/${key}`;
}

/**
 * Reads/writes `localStorage` under {@link scopedStorageKey}.
 */
export const scopedLocalStorage = {
  getItem(logicalKey: string): string | null {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(scopedStorageKey(logicalKey));
  },

  setItem(logicalKey: string, value: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(scopedStorageKey(logicalKey), value);
  },

  removeItem(logicalKey: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(scopedStorageKey(logicalKey));
  },
};

/**
 * Reads/writes `sessionStorage` under {@link scopedStorageKey}.
 */
export const scopedSessionStorage = {
  getItem(logicalKey: string): string | null {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(scopedStorageKey(logicalKey));
  },

  setItem(logicalKey: string, value: string): void {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(scopedStorageKey(logicalKey), value);
  },

  removeItem(logicalKey: string): void {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(scopedStorageKey(logicalKey));
  },
};

function createPrefixedUnderlyingLocalStorage(): StateStorage {
  return {
    getItem: (name) => scopedLocalStorage.getItem(name),
    setItem: (name, value) => scopedLocalStorage.setItem(name, value),
    removeItem: (name) => scopedLocalStorage.removeItem(name),
  };
}

/**
 * JSON `localStorage` adapter for Zustand `persist`; keys match the middleware `name` option and are scoped.
 */
export function createScopedJSONStorage() {
  return createJSONStorage(() =>
    typeof window === 'undefined' ? noopStorage : createPrefixedUnderlyingLocalStorage()
  );
}
